import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { m } from '~/paraglide/messages.js';
import type { KYCStatusResponse } from '../-api/api';

interface KYCIframeProps {
  kycData: KYCStatusResponse;
}

export function KYCIframe({ kycData }: KYCIframeProps) {
  const [iframeLoading, setIframeLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const hasLoadedOnce = useRef(false);

  const handleIframeLoad = useCallback(() => {
    if (!hasLoadedOnce.current) {
      hasLoadedOnce.current = true;
      setIframeLoading(false);
      return;
    }

    try {
      const iframeUrl = iframeRef.current?.contentWindow?.location.href;
      if (iframeUrl?.startsWith(window.location.origin)) {
        navigate({ to: '/' });
      }
    } catch {
      // Cross-origin — AMLBot is still showing its own pages
    }
  }, [navigate]);

  return (
    <div className="flex flex-col h-full min-h-screen bg-background">
      <div className="p-4 border-b pb-12" />

      <div className="relative flex-1 w-full min-h-0">
        {iframeLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {m['verify_identity.kyc_iframe_loading']()}
              </p>
            </div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={kycData.verification_url}
          className="absolute inset-0 w-full h-full border-0"
          title={m['verify_identity.kyc_iframe_title']()}
          onLoad={handleIframeLoad}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads"
          allow="camera; microphone; geolocation; fullscreen"
          allowFullScreen
        />
      </div>
    </div>
  );
}
